/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";
import { useTranslation } from "react-i18next";
import { GiErlenmeyer } from "react-icons/gi";

interface DispensingSummaryandFineTuningProps {
  batchDetails: any;
}

const DispensingSummaryandFineTuning: React.FC<DispensingSummaryandFineTuningProps> = ({
  batchDetails,
}) => {
  const { t } = useTranslation();
  const dispensingSummary = batchDetails?.dispensing_summary;
  const powderConsumption = batchDetails?.powder_consumption?.[0];
  const fineTuning = batchDetails?.fine_tuning_details;

  const containerUsed = dispensingSummary?.quantity_dispensed || "-";
  const totalFilled = dispensingSummary?.total_dispensed_volume_ml || "-";
  const recordedWaste = dispensingSummary?.recorded_waste_units || "-";
  const completed = dispensingSummary?.dispensing_status || "-";
  const observedFinalPH = fineTuning?.observed_final_ph || "-";
  const checkedby = fineTuning?.performed_by_name || "-";
  const checkedOn = fineTuning?.created_at || "-";
  const additiveLot = fineTuning?.additive_lot || "-";
  const additiveProductName = fineTuning?.additive_product_name || "-";



  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              {/* 5. Dispensing Summary */}
              <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div
                  className="flex items-center bg-[#F8FAFC] gap-4 px-6 py-2"
                  style={{borderBottom: "1px solid #D6DEE8",}}
                >
                  <div className="w-6 h-6 rounded-sm bg-slate-900 text-white flex items-center justify-center font-black text-[10px]">05</div>
                  <h3 className="text-[13px] font-bold uppercase tracking-wide text-[#1E2B4D] mt-[7px]">
                    {t("mediaPrepBatch.mediaPrepBatchDetails.DispensingSummary")}
                  </h3>
                </div>
                <div className="px-5">
                  <div className="flex justify-between items-center py-1" style={{ borderBottom: "1px solid #D6DEE8" }}>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('mediaPrepBatch.mediaPrepBatchDetails.ContainerType')}</p>
                    <p className="text-sm font-black text-slate-800">{containerUsed}</p>
                  </div>

                  <div className="flex justify-between items-center py-1" style={{ borderBottom: "1px solid #D6DEE8" }}>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('mediaPrepBatch.mediaPrepBatchDetails.TotalFilled')}</p>
                    <p className="text-sm font-black text-slate-800">{totalFilled}</p>
                  </div>

                  <div className="py-2.5">
                    <div className="w-[280px] mb-1 px-4 py-0.1 rounded-md border border-green-200 bg-green-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xl"><GiErlenmeyer className="text-green-700 text-xl" /></div>
                        <div className="flex flex-col">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase leading-none mt-3">Dispensing Status</p>
                          <p className="text-[14px] font-bold text-green-700 leading-none -mt-2">{completed}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 6. Fine-Tuning */}
              <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="flex items-center bg-[#F8FAFC] gap-4 px-6 py-2" style={{ borderBottom: "1px solid #D6DEE8" }}>
                  <div className="w-6 h-6 rounded-sm bg-slate-900 text-white flex items-center justify-center font-black text-[10px]">06</div>
                  <h3 className="text-[13px] font-bold uppercase tracking-wide text-[#1E2B4D] mt-[7px]">{t("mediaPrepBatch.mediaPrepBatchDetails.AdjustmentDetails")}</h3>
                </div>

                <div className="px-5">
                  <div className="flex justify-between items-center py-1" style={{ borderBottom: "1px solid #D6DEE8" }}>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t("mediaPrepBatch.mediaPrepBatchDetails.PhAdjustedTo")}</p>
                    <p className="text-sm font-black text-slate-800">
                      {fineTuning
                        ? `${powderConsumption?.final_ph} ± ${powderConsumption?.final_ph_tolerance}`
                        : "-"}
                    </p>
                  </div>

                  <div className="flex justify-between items-center py-1" style={{ borderBottom: "1px solid #D6DEE8" }}>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t("mediaPrepBatch.mediaPrepBatchDetails.FinalpH")}</p>
                    <p className="text-sm font-black text-emerald-600">{observedFinalPH} pH</p>
                  </div>

                  <div className="flex justify-between items-center py-1" style={{ borderBottom: "1px solid #D6DEE8" }}>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t("mediaPrepBatch.mediaPrepBatchDetails.CheckedBy")}</p>
                    <p className="text-sm font-black text-slate-800">{checkedby}</p>
                  </div>

                  <div className="flex justify-between items-center py-1">
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t("mediaPrepBatch.mediaPrepBatchDetails.CheckedOn")}</p>
                    <p className="text-sm font-black text-slate-800">{checkedOn}</p>
                  </div>
                </div>
              </section>
            </div>
  );
};

export default DispensingSummaryandFineTuning;